import React from "react";

/**
 * PlatformIcon – renderer offisielle merkevare-logoer via Simple Icons CDN
 * (https://simpleicons.org/, CC0-lisens). For plattformer som ikke finnes
 * i SimpleIcons (Fansly, LoyalFans, ManyVids, m.fl.) bruker vi Google
 * favicon-tjenesten som fallback.
 *
 * Hvis plattformen ikke finnes i mappingen og lenken ikke har noen URL vi
 * kan utlede domene fra, returneres null så LinkButton faller tilbake til
 * ren tekst-presentasjon.
 */
const PLATFORM_MAP = {
  // Voksen / creator-plattformer
  "onlyfans": { slug: "onlyfans", color: "00AFF0" },
  "fansly": { favicon_domain: "fansly.com" },
  "loyalfans": { favicon_domain: "loyalfans.com" },
  "manyvids": { favicon_domain: "manyvids.com" },
  "pornhub": { slug: "pornhub", color: "FF9900" },
  "fanvue": { slug: "fanvue", color: "5856E5" },
  "loverfans": { favicon_domain: "loverfans.com" },
  "justforfans": { favicon_domain: "justfor.fans" },
  "ismygirl": { favicon_domain: "ismygirl.com" },

  // Tips / støtte
  "patreon": { slug: "patreon", color: "FF424D" },
  "buy me a coffee": { slug: "buymeacoffee", color: "FFDD00" },
  "buymeacoffee": { slug: "buymeacoffee", color: "FFDD00" },
  "ko-fi": { slug: "kofi", color: "FF5E5B" },
  "kofi": { slug: "kofi", color: "FF5E5B" },

  // Sosiale medier
  "instagram": { slug: "instagram", color: "E4405F" },
  "x": { slug: "x", color: "FFFFFF" },
  "twitter": { slug: "x", color: "FFFFFF" },
  "x / twitter": { slug: "x", color: "FFFFFF" },
  "tiktok": { slug: "tiktok", color: "FFFFFF" },
  "snapchat": { slug: "snapchat", color: "FFFC00" },
  "telegram": { slug: "telegram", color: "26A5E4" },
  "bluesky": { slug: "bluesky", color: "0285FF" },
  "youtube": { slug: "youtube", color: "FF0000" },
  "facebook": { slug: "facebook", color: "1877F2" },
  "reddit": { slug: "reddit", color: "FF4500" },
  "discord": { slug: "discord", color: "5865F2" },
  "twitch": { slug: "twitch", color: "9146FF" },
  "pinterest": { slug: "pinterest", color: "BD081C" },
  "threads": { slug: "threads", color: "FFFFFF" },

  // Link-i-bio
  "linktree": { slug: "linktree", color: "43E660" },
  "beacons": { slug: "beacons", color: "5C6CFA" },
  "linkin.bio": { slug: "later", color: "8855FF" },
};

function domainFromUrl(url) {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

export default function PlatformIcon({ platform, url, size = 22, className = "" }) {
  const key = (platform || "").trim().toLowerCase();
  // Eksakt treff først
  let cfg = key ? PLATFORM_MAP[key] : null;
  // Fuzzy fallback: hvis brukeren skrev "OnlyFans VIP", finn første kjente
  // plattform-navn som forekommer i strengen.
  if (!cfg && key) {
    const found = Object.keys(PLATFORM_MAP).find((k) => key.includes(k));
    if (found) cfg = PLATFORM_MAP[found];
  }

  // Bestem bilde-URL: SimpleIcons hvis tilgjengelig, ellers Google favicon
  // basert på domene (enten fra config eller fra URL-en lenken peker på).
  let src = null;
  let isSimpleicon = false;
  if (cfg?.slug) {
    src = `https://cdn.simpleicons.org/${cfg.slug}/${cfg.color}`;
    isSimpleicon = true;
  } else {
    const domain = cfg?.favicon_domain || domainFromUrl(url);
    if (domain) {
      src = `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
    }
  }
  if (!src) return null;

  return (
    <img
      src={src}
      alt={platform || ""}
      width={size}
      height={size}
      loading="lazy"
      className={className}
      style={{ display: "inline-block", flexShrink: 0, borderRadius: isSimpleicon ? 0 : 4 }}
      onError={(e) => { e.target.style.display = "none"; }}
      data-testid={`platform-icon-${cfg?.slug || "favicon"}`}
    />
  );
}

export { PLATFORM_MAP };
